import { Autocomplete, Button, Icon, TextField, Tooltip } from "@mui/material";
import useCatchApiError from "hooks/useCatchApiError";
import useFlowRoles from "hooks/useFlowRoles";
import { IConsumidor } from "model/cliente.model";
import CadastroPessoa from "pages/cadastro-pessoa/CadastroPessoa";
import RegistraVendaService from "pages/registra-venda/registra-venda.service";
import React, { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { theme } from "theme";

type Props = {
    consumidor: IConsumidor | null;
    disable?: boolean;
    onSelect: (value: IConsumidor | null, reason: 'props' | 'click') => void;
    hideAddPessoa?: boolean;
    label?: string;
};

export type ConsumidorInputComponent = {
    focus: () => void;
};

type LocalState = {
    consumidores: IConsumidor[],
    consumidoresFiltro: string,
    shouldShowTooltip: boolean;
};

const initialState: LocalState = {
    consumidores: [],
    consumidoresFiltro: '',
    shouldShowTooltip: false,
};

const ConsumidorInputInner = (props: Props, ref: Ref<ConsumidorInputComponent>) => {
    const [localState, setLocalState] = useState(initialState);
    const [openCadastroPessoaDialog, setOpenCadastroPessoaDialog] = useState(false);
    const service = RegistraVendaService();
    const { cadastroPessoa } = useFlowRoles();
    const { throwErrorMessage } = useCatchApiError();

    const clienteRef = useRef<HTMLInputElement>(null);
    const canUserAddPessoaRef = useRef<boolean>(cadastroPessoa.canNovo || cadastroPessoa.canEditar);

    useImperativeHandle(ref, () => ({
        focus,
    }));

    useEffect(() => {
        getConsumidoresAc();

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!openCadastroPessoaDialog) {
            props.onSelect(props.consumidor, 'props');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openCadastroPessoaDialog]);

    const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'F2':
                e.preventDefault();
                clienteRef.current && clienteRef.current.focus();
                break;
        }
    };

    const getConsumidoresAc = useCallback((toSearch: string = '') => {
        service.getConsumidoresAc(toSearch)
            .then(({ data }) => {
                setLocalState(prevState => ({ ...prevState, consumidores: data }));
            }).catch(error => {
                throwErrorMessage(error);
            });
        //eslint-disable-next-line
    }, []);

    const onInputChangeConsumidoresAc = (value: string) => {
        getConsumidoresAc(value);
        setLocalState(prevState => ({ ...prevState, consumidoresFiltro: value }));
    };

    const onBlurConsumidores = () => {
        if (!localState.consumidoresFiltro && !props.consumidor) {
            getConsumidoresAc();
        }
    };

    const focus = () => {
        setTimeout(() => {
            clienteRef.current?.focus();
        },);
    };

    return <>
        <Autocomplete
            disabled={props.disable}
            onBlur={onBlurConsumidores}
            value={props.consumidor}
            disablePortal
            id="acCliente"
            options={localState.consumidores}
            getOptionLabel={(option) => option.descritivo}
            onInputChange={(_, value) => onInputChangeConsumidoresAc(value)}
            filterOptions={(x) => x}
            noOptionsText={'Nenhum resultado encontrado'}
            onChange={(_, value) => props.onSelect(value, 'click')}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            renderOption={(props, option) => {
                return (
                    <li {...props} key={option.uuid}>
                        {option.descritivo}
                    </li>
                );
            }}
            renderInput={
                (params) => <TextField {...params}
                    label={props.label ?? "Consumidor"}
                    variant="standard"
                    inputRef={clienteRef}

                    sx={{
                        '& .MuiInputBase-root.Mui-disabled input': {
                            WebkitTextFillColor: theme.palette.text.disabled,
                            color: theme.palette.text.disabled,
                        },
                    }}
                    InputLabelProps={{
                        sx: {
                            marginLeft: '5px',
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                <h4 className="h4-formatado">(F2)</h4>

                                {!props.hideAddPessoa &&
                                    <Tooltip
                                        disableHoverListener={canUserAddPessoaRef.current}
                                        title="Usuário sem acesso ao cadastro de pessoas"
                                        arrow
                                        slotProps={{
                                            popper: {
                                                modifiers: [
                                                    {
                                                        name: 'offset',
                                                        options: {
                                                            offset: [0, -10],
                                                        },
                                                    },
                                                ],
                                            },
                                        }}
                                    >
                                        <span>
                                            <Button
                                                disabled={!canUserAddPessoaRef.current || props.disable}
                                                sx={{
                                                    width: 'fit-content',
                                                    minWidth: 'auto',
                                                    marginRight: '-10px'
                                                }}
                                                onClick={() => {
                                                    setOpenCadastroPessoaDialog(true);
                                                }}
                                            >
                                                {!props.consumidor ? <Icon
                                                    className="fa-solid fa-user-plus"
                                                    sx={{
                                                        width: 'fit-content',
                                                        fontSize: '18px',
                                                        color: !canUserAddPessoaRef.current || props.disable ? 'grey' : theme.palette.primary.main
                                                    }}
                                                /> : <Icon
                                                    className="fa-solid fa-pencil"
                                                    sx={{
                                                        width: 'fit-content',
                                                        fontSize: '18px',
                                                        color: (!canUserAddPessoaRef.current || props.disable) ? 'grey' : theme.palette.primary.main
                                                    }}
                                                />
                                                }
                                            </Button>
                                        </span>
                                    </Tooltip>
                                }
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        )
                    }}
                />}
        />

        <CadastroPessoa
            open={openCadastroPessoaDialog}
            setOpen={setOpenCadastroPessoaDialog}
        />
    </>;
};

const ConsumidorInput = forwardRef(ConsumidorInputInner);

export default ConsumidorInput;
